import React, { useState } from 'react';
import type { ConventionReportSummary } from '@/services/useConventionReport';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, CheckCircle, Clock, Banknote, CreditCard, Ticket, Mail, XCircle, Building2, Download, Search, ChevronRight, CornerDownRight, Info } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from '@/components/ui/table';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const formatRp = (n: number) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(n);

interface SummarySectionProps {
  data: ConventionReportSummary;
  onInstitutionClick?: (institution: string) => void;
  onGuestInstitutionClick?: (institution: string) => void;
}

export function SummarySection({ data, onInstitutionClick, onGuestInstitutionClick }: SummarySectionProps) {
  const p = data.participants;
  const g = data.event_guests;
  const [searchInst, setSearchInst] = useState('');
  const [guestSearchInst, setGuestSearchInst] = useState('');
  const [combinedSearchInst, setCombinedSearchInst] = useState('');
  const [participantPage, setParticipantPage] = useState(1);
  const [guestPage, setGuestPage] = useState(1);
  const [combinedPage, setCombinedPage] = useState(1);
  const [showAllParticipants, setShowAllParticipants] = useState(false);
  const [showAllGuests, setShowAllGuests] = useState(false);
  const [showAllCombined, setShowAllCombined] = useState(false);
  const [expandedGuests, setExpandedGuests] = useState<Record<string, boolean>>({});
  const [expandedParticipants, setExpandedParticipants] = useState<Record<string, boolean>>({});
  const itemsPerPage = 15;

  const toggleGuestRow = (institution: string) => {
    setExpandedGuests(prev => ({ ...prev, [institution]: !prev[institution] }));
  };

  const toggleParticipantRow = (institution: string) => {
    setExpandedParticipants(prev => ({ ...prev, [institution]: !prev[institution] }));
  };

  const combinedInstitutions = React.useMemo(() => {
    const map: Record<string, { institution: string, participants: number, guests: number, total: number, printed: number, rfid: number }> = {};
    
    p.per_institution.forEach(inst => {
      if (!map[inst.institution]) {
        map[inst.institution] = { institution: inst.institution, participants: 0, guests: 0, total: 0, printed: 0, rfid: 0 };
      }
      map[inst.institution].participants += inst.paid;
      map[inst.institution].total += inst.paid;
      map[inst.institution].printed += (inst.is_printed || 0);
      map[inst.institution].rfid += (inst.with_rfid || 0);
    });

    g.per_institution.forEach(inst => {
      if (!map[inst.institution]) {
        map[inst.institution] = { institution: inst.institution, participants: 0, guests: 0, total: 0, printed: 0, rfid: 0 };
      }
      map[inst.institution].guests += inst.total;
      map[inst.institution].total += inst.total;
      map[inst.institution].printed += (inst.is_printed || 0);
      map[inst.institution].rfid += (inst.with_rfid || 0);
    });

    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [p.per_institution, g.per_institution]);

  const filteredInstitutions = p.per_institution.filter(inst => 
    inst.institution.toLowerCase().includes(searchInst.toLowerCase())
  );

  const filteredGuestInstitutions = g.per_institution.filter(inst => 
    inst.institution.toLowerCase().includes(guestSearchInst.toLowerCase())
  );

  const filteredCombinedInstitutions = combinedInstitutions.filter(inst => 
    inst.institution.toLowerCase().includes(combinedSearchInst.toLowerCase())
  );

  
  const handleExportGuestInstitutions = () => {
    const headers = ['Nama Institusi', 'Total Tamu & Panitia', 'Tercetak', 'Scan RFID', 'Selisih (Tercetak - RFID)'];
    const dataToExport = guestSearchInst ? filteredGuestInstitutions : g.per_institution;
    
    const rows = dataToExport.map(inst => [
      inst.institution,
      inst.total,
      inst.is_printed,
      inst.with_rfid,
      (inst.is_printed || 0) - (inst.with_rfid || 0)
    ]);
    
    const totalCount = dataToExport.reduce((acc, curr) => acc + (curr.total || 0), 0);
    const totalPrinted = dataToExport.reduce((acc, curr) => acc + (curr.is_printed || 0), 0);
    const totalRfid = dataToExport.reduce((acc, curr) => acc + (curr.with_rfid || 0), 0);

    rows.push([
      guestSearchInst ? `TOTAL KESELURUHAN (Pencarian: ${guestSearchInst})` : 'TOTAL KESELURUHAN (Semua Institusi)',
      totalCount,
      totalPrinted,
      totalRfid,
      totalPrinted - totalRfid
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(r => r.map(v => `"${v}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `rekap_institusi_tamu_periode_${data.period.period_id}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

const handleExportInstitutions = () => {
    const headers = ['Nama Institusi', 'Total Peserta', 'Lunas', 'Belum Bayar', 'Tercetak', 'Scan RFID', 'Selisih (Tercetak - RFID)', 'Total Pendapatan (Lunas)'];
    // Export either all or filtered based on search, usually it's best to export what is currently filtered
    const dataToExport = searchInst ? filteredInstitutions : p.per_institution;
    
    const rows = dataToExport.map(inst => [
      inst.institution,
      inst.total,
      inst.paid,
      inst.unpaid,
      inst.is_printed,
      inst.with_rfid,
      (inst.is_printed || 0) - (inst.with_rfid || 0),
      inst.revenue_paid || 0
    ]);
    
    const totalCount = dataToExport.reduce((acc, curr) => acc + (curr.total || 0), 0);
    const totalPaid = dataToExport.reduce((acc, curr) => acc + (curr.paid || 0), 0);
    const totalUnpaid = dataToExport.reduce((acc, curr) => acc + (curr.unpaid || 0), 0);
    const totalPrinted = dataToExport.reduce((acc, curr) => acc + (curr.is_printed || 0), 0);
    const totalRfid = dataToExport.reduce((acc, curr) => acc + (curr.with_rfid || 0), 0);
    const totalRevenue = dataToExport.reduce((acc, curr) => acc + (curr.revenue_paid || 0), 0);

    rows.push([
      searchInst ? `TOTAL KESELURUHAN (Pencarian: ${searchInst})` : 'TOTAL KESELURUHAN (Semua Institusi)',
      totalCount,
      totalPaid,
      totalUnpaid,
      totalPrinted,
      totalRfid,
      totalPrinted - totalRfid,
      totalRevenue
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(r => r.map(v => `"${v}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `rekap_institusi_periode_${data.period.period_id}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportCombinedInstitutions = () => {
    const headers = ['Nama Institusi', 'Total Peserta', 'Total Tamu & Panitia', 'Tercetak', 'Scan RFID', 'Selisih', 'Total Keseluruhan'];
    const dataToExport = combinedSearchInst ? filteredCombinedInstitutions : combinedInstitutions;
    
    const rows = dataToExport.map(inst => [
      inst.institution,
      inst.participants,
      inst.guests,
      inst.printed,
      inst.rfid,
      (inst.printed || 0) - (inst.rfid || 0),
      inst.total
    ]);
    
    const totalParticipants = dataToExport.reduce((acc, curr) => acc + (curr.participants || 0), 0);
    const totalGuests = dataToExport.reduce((acc, curr) => acc + (curr.guests || 0), 0);
    const totalPrinted = dataToExport.reduce((acc, curr) => acc + (curr.printed || 0), 0);
    const totalRfid = dataToExport.reduce((acc, curr) => acc + (curr.rfid || 0), 0);
    const totalAll = dataToExport.reduce((acc, curr) => acc + (curr.total || 0), 0);

    rows.push([
      combinedSearchInst ? `TOTAL KESELURUHAN (Pencarian: ${combinedSearchInst})` : 'TOTAL KESELURUHAN (Semua Institusi)',
      totalParticipants,
      totalGuests,
      totalPrinted,
      totalRfid,
      totalPrinted - totalRfid,
      totalAll
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(r => r.map(v => `"${v}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `rekap_keseluruhan_institusi_periode_${data.period.period_id}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6">


      <div>
        <h3 className="text-lg font-semibold mb-3 flex items-center">
          <Users className="w-5 h-5 mr-2 text-primary" /> 
          Ringkasan Peserta
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard title="Total Peserta" value={p.total} bgClass="bg-gradient-to-br from-blue-50 dark:from-blue-900/20 to-slate-100 dark:to-slate-800/80" icon={<Users className="w-5 h-5 text-blue-600 dark:text-blue-500" />} />
          <StatCard title="Lunas" value={p.paid} bgClass="bg-gradient-to-br from-emerald-50 dark:from-emerald-900/20 to-green-100 dark:to-emerald-800/20" valueClassName="text-emerald-700 dark:text-emerald-400" icon={<CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-500" />} />
          <StatCard title="Belum Bayar" value={p.unpaid} bgClass="bg-gradient-to-br from-rose-50 dark:from-rose-900/20 to-red-100 dark:to-rose-800/20" valueClassName="text-rose-700 dark:text-rose-400" icon={<XCircle className="w-5 h-5 text-rose-600 dark:text-rose-500" />} />
          <StatCard title="Menunggu Konfirmasi" value={p.pending} bgClass="bg-gradient-to-br from-amber-50 dark:from-amber-900/20 to-yellow-100 dark:to-amber-800/20" valueClassName="text-amber-700 dark:text-amber-400" icon={<Clock className="w-5 h-5 text-amber-600" />} />
          
          <StatCard title="Total Omzet (Kotor)" value={formatRp(p.total_revenue)} bgClass="bg-gradient-to-br from-slate-50 dark:from-slate-800 to-gray-100 dark:to-slate-800/20" valueClassName="text-slate-700 dark:text-slate-200" icon={<Banknote className="w-5 h-5 text-slate-600 dark:text-slate-300" />} />
          <StatCard title="Omzet Masuk (Lunas)" value={formatRp(p.total_revenue_paid)} bgClass="bg-gradient-to-br from-emerald-50 dark:from-emerald-900/20 to-green-100 dark:to-emerald-800/20" valueClassName="text-emerald-700 dark:text-emerald-400" icon={<Banknote className="w-5 h-5 text-emerald-600 dark:text-emerald-500" />} />
          
          <StatCard title="Terdaftar RFID" value={p.with_rfid} bgClass="bg-gradient-to-br from-indigo-50 dark:from-indigo-900/20 to-blue-100 dark:to-blue-800/20" valueClassName="text-indigo-700 dark:text-indigo-400" icon={<CreditCard className="w-5 h-5 text-indigo-600" />} />
          <StatCard title="Kartu Tercetak" value={p.is_printed} bgClass="bg-gradient-to-br from-purple-50 dark:from-purple-900/20 to-fuchsia-100 dark:to-fuchsia-800/20" valueClassName="text-purple-700 dark:text-purple-400" icon={<Ticket className="w-5 h-5 text-purple-600 dark:text-purple-500" />} />
        </div>
      </div>

      <div className="pt-4 border-t border-border mt-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 gap-4">
          <h3 className="text-lg font-bold flex items-center text-slate-800 dark:text-white">
            <div className="bg-primary/10 p-2 rounded-lg mr-3">
              <Building2 className="w-5 h-5 text-primary" /> 
            </div>
            Top Institusi / Universitas Teratas
          </h3>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 shrink-0">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
              <Input 
                placeholder="Cari institusi..." 
                value={searchInst}
                onChange={(e) => {
                  setSearchInst(e.target.value);
                  setParticipantPage(1);
                }}
                className="pl-9 h-9 text-sm w-full sm:w-64 bg-white dark:bg-slate-900"
              />
            </div>
            <Button variant="outline" size="sm" onClick={handleExportInstitutions} className="text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-900 h-9">
              <Download className="w-4 h-4 mr-2" />
              Export Institusi
            </Button>
          </div>
        </div>
        
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700/60 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-slate-50 dark:bg-slate-800/50">
                <TableRow className="border-b border-slate-100 dark:border-slate-800 hover:bg-transparent">
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 py-4 px-6">Nama Institusi</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 text-center py-4 px-6">Total Peserta</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-emerald-600 dark:text-emerald-500 text-center py-4 px-6">Lunas</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-rose-500 dark:text-rose-400 text-center py-4 px-6">Belum Bayar</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-purple-600 dark:text-purple-500 text-center py-4 px-6">Tercetak</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-blue-600 dark:text-blue-500 text-center py-4 px-6">Scan RFID</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-amber-600 dark:text-amber-500 text-center py-4 px-6">
                    <div className="flex items-center justify-center gap-1">
                      Selisih
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="cursor-help inline-flex items-center">
                            <Info className="w-3.5 h-3.5 opacity-70" />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Selisih (Tercetak - Scan RFID)</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 text-right py-4 px-6 sticky right-[56px] bg-slate-50 dark:bg-slate-800/50 shadow-[-12px_0_15px_-10px_rgba(0,0,0,0.05)] z-20 before:absolute before:inset-y-0 before:left-0 before:w-[1px] before:bg-slate-200 dark:before:bg-slate-800">Total Pendapatan</TableHead>
                  <TableHead className="py-4 px-6 w-14 sticky right-0 bg-slate-50 dark:bg-slate-800/50 z-20"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(showAllParticipants ? filteredInstitutions : filteredInstitutions.slice((participantPage - 1) * itemsPerPage, participantPage * itemsPerPage)).map((inst, idx) => (
                  <React.Fragment key={idx}>
                  <TableRow 
                    className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors border-b border-slate-100 dark:border-slate-800/50 last:border-0 cursor-pointer group"
                    onClick={() => toggleParticipantRow(inst.institution)}
                  >
                    <TableCell className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <span className="text-[13px] font-medium text-slate-400 dark:text-slate-500 w-5 tabular-nums text-right shrink-0">{String(idx + 1).padStart(2, '0')}</span>
                        <div className={`flex items-center justify-center w-5 h-5 rounded transition-transform duration-200 ${expandedParticipants[inst.institution] ? 'text-primary rotate-90' : 'text-slate-300 dark:text-slate-600 group-hover:text-slate-500 dark:text-slate-400'} shrink-0`}>
                          <ChevronRight className="w-4 h-4" />
                        </div>
                        <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700/60 flex items-center justify-center shrink-0 shadow-sm">
                          <Building2 className="w-3.5 h-3.5 text-slate-500 dark:text-slate-400" />
                        </div>
                        <span className="font-medium text-slate-700 dark:text-slate-200 text-[14px] group-hover:text-slate-900 dark:group-hover:text-white transition-colors">{inst.institution}</span>
                      </div>
                    </TableCell>
                    <TableCell className="py-4 px-6 text-center">
                      <span className="text-[14px] font-semibold text-slate-700 dark:text-slate-200 tabular-nums">
                        {inst.total}
                      </span>
                    </TableCell>
                    <TableCell className="py-4 px-6 text-center">
                      <div className="flex items-center justify-center gap-2">
                        {inst.paid > 0 ? (
                          <>
                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]"></div>
                            <span className="text-[14px] font-semibold text-emerald-700 dark:text-emerald-400 tabular-nums">{inst.paid}</span>
                          </>
                        ) : (
                          <span className="text-slate-300 dark:text-slate-600 font-medium">-</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="py-4 px-6 text-center">
                      <div className="flex items-center justify-center gap-2">
                        {inst.unpaid > 0 ? (
                          <>
                            <div className="w-1.5 h-1.5 rounded-full bg-rose-500"></div>
                            <span className="text-[14px] font-semibold text-rose-600 dark:text-rose-500 tabular-nums">{inst.unpaid}</span>
                          </>
                        ) : (
                          <span className="text-slate-300 dark:text-slate-600 font-medium">-</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="py-4 px-6 text-center">
                      <div className="flex items-center justify-center gap-2">
                        {inst.is_printed > 0 ? (
                          <>
                            <div className="w-1.5 h-1.5 rounded-full bg-purple-500 shadow-[0_0_8px_rgba(168,85,247,0.5)]"></div>
                            <span className="text-[14px] font-semibold text-purple-700 tabular-nums">{inst.is_printed}</span>
                          </>
                        ) : (
                          <span className="text-slate-300 dark:text-slate-600 font-medium">-</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="py-4 px-6 text-center">
                      <div className="flex items-center justify-center gap-2">
                        {inst.with_rfid > 0 ? (
                          <>
                            <div className="w-1.5 h-1.5 rounded-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.5)]"></div>
                            <span className="text-[14px] font-semibold text-blue-700 tabular-nums">{inst.with_rfid}</span>
                          </>
                        ) : (
                          <span className="text-slate-300 dark:text-slate-600 font-medium">-</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="py-4 px-6 text-center">
                      <span className={`text-[14px] font-semibold tabular-nums ${(inst.is_printed || 0) - (inst.with_rfid || 0) > 0 ? 'text-amber-600 dark:text-amber-500' : 'text-slate-700 dark:text-slate-200'}`}>
                        {(inst.is_printed || 0) - (inst.with_rfid || 0)}
                      </span>
                    </TableCell>
                    <TableCell className="py-4 px-6 text-right sticky right-[56px] bg-white dark:bg-slate-900 group-hover:bg-slate-50 dark:group-hover:bg-slate-800/50 z-10 transition-colors shadow-[-12px_0_15px_-10px_rgba(0,0,0,0.05)] before:absolute before:inset-y-0 before:left-0 before:w-[1px] before:bg-slate-100 dark:before:bg-slate-800/50">
                      <span className="text-[14px] font-semibold text-slate-700 dark:text-slate-200 tabular-nums tracking-tight">
                        {formatRp(inst.revenue_paid || 0)}
                      </span>
                    </TableCell>
                    <TableCell className="py-4 px-4 text-center sticky right-0 bg-white dark:bg-slate-900 group-hover:bg-slate-50 dark:group-hover:bg-slate-800/50 z-10 transition-colors">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8 text-slate-400 dark:text-slate-500 hover:text-primary hover:bg-primary/10 rounded-full"
                        onClick={(e) => { e.stopPropagation(); onInstitutionClick?.(inst.institution); }}
                        title="Lihat Detail Peserta"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                  {expandedParticipants[inst.institution] && inst.breakdown && inst.breakdown.length > 0 && (
                    <>
                      {inst.breakdown.map((b, i) => (
                        <TableRow key={`sub-${idx}-${i}`} className="bg-slate-50/50 dark:bg-slate-800/40 border-b border-slate-200 dark:border-slate-700/60 hover:bg-slate-100/50 dark:hover:bg-slate-800/60 transition-colors">
                          <TableCell className="py-2.5 px-6">
                            <div className="flex items-center gap-3 pl-[6.5rem]">
                              <CornerDownRight className="w-4 h-4 text-slate-300 dark:text-slate-600 shrink-0" />
                              <span className="text-[13px] font-medium text-slate-600 dark:text-slate-300">{b.category}</span>
                            </div>
                          </TableCell>
                          <TableCell className="py-2.5 px-6 text-center">
                            <span className="text-[13px] font-semibold text-slate-600 dark:text-slate-300 tabular-nums">{b.total}</span>
                          </TableCell>
                          <TableCell className="py-2.5 px-6 text-center">
                            <span className="text-[13px] font-semibold text-emerald-600 dark:text-emerald-500 tabular-nums">{b.paid > 0 ? b.paid : <span className="text-slate-300 dark:text-slate-600 font-medium">-</span>}</span>
                          </TableCell>
                          <TableCell className="py-2.5 px-6 text-center">
                            <span className="text-[13px] font-semibold text-rose-600 dark:text-rose-500 tabular-nums">{b.unpaid > 0 ? b.unpaid : <span className="text-slate-300 dark:text-slate-600 font-medium">-</span>}</span>
                          </TableCell>
                          <TableCell className="py-2.5 px-6 text-center">
                            <span className="text-[13px] font-semibold text-purple-600 dark:text-purple-500 tabular-nums">{b.is_printed > 0 ? b.is_printed : <span className="text-slate-300 dark:text-slate-600 font-medium">-</span>}</span>
                          </TableCell>
                          <TableCell className="py-2.5 px-6 text-center">
                            <span className="text-[13px] font-semibold text-blue-600 dark:text-blue-500 tabular-nums">{b.with_rfid > 0 ? b.with_rfid : <span className="text-slate-300 dark:text-slate-600 font-medium">-</span>}</span>
                          </TableCell>
                          <TableCell className="py-2.5 px-6 text-center">
                            <span className={`text-[13px] font-semibold tabular-nums ${(b.is_printed || 0) - (b.with_rfid || 0) > 0 ? 'text-amber-600 dark:text-amber-500' : 'text-slate-600 dark:text-slate-300'}`}>
                              {(b.is_printed || 0) - (b.with_rfid || 0) > 0 ? (b.is_printed || 0) - (b.with_rfid || 0) : <span className="text-slate-300 dark:text-slate-600 font-medium">-</span>}
                            </span>
                          </TableCell>
                          <TableCell className="py-2.5 px-6 text-right sticky right-[56px] bg-slate-50/50 dark:bg-slate-800/40 group-hover:bg-slate-100/50 dark:group-hover:bg-slate-800/60 z-10 transition-colors shadow-[-12px_0_15px_-10px_rgba(0,0,0,0.05)] before:absolute before:inset-y-0 before:left-0 before:w-[1px] before:bg-slate-200 dark:before:bg-slate-700/60">
                            <span className="text-[13px] font-semibold text-slate-600 dark:text-slate-300 tabular-nums tracking-tight">{formatRp(b.revenue_paid || 0)}</span>
                          </TableCell>
                          <TableCell className="py-2.5 px-4 sticky right-0 bg-slate-50/50 dark:bg-slate-800/40 group-hover:bg-slate-100/50 dark:group-hover:bg-slate-800/60 z-10 transition-colors"></TableCell>
                        </TableRow>
                      ))}
                    </>
                  )}
                  </React.Fragment>
                ))}
                {filteredInstitutions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-12 text-slate-500 dark:text-slate-400">
                      <Building2 className="w-10 h-10 mx-auto mb-4 text-slate-200" />
                      <span className="font-medium text-slate-600 dark:text-slate-300">Institusi tidak ditemukan</span>
                      <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">Coba gunakan kata kunci pencarian yang lain.</p>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              {p.per_institution.length > 0 && (
                <TableFooter className="bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700">
                  <TableRow className="hover:bg-transparent">
                    <TableCell className="font-bold text-slate-700 dark:text-slate-200 text-sm py-4 px-6">TOTAL KESELURUHAN (Semua Institusi)</TableCell>
                    <TableCell className="text-center font-bold text-slate-800 dark:text-slate-100 text-sm py-4 px-6">{p.total}</TableCell>
                    <TableCell className="text-center font-bold text-emerald-700 dark:text-emerald-400 text-sm py-4 px-6">{p.paid}</TableCell>
                    <TableCell className="text-center font-bold text-rose-600 dark:text-rose-500 text-sm py-4 px-6">{p.unpaid}</TableCell>
                    <TableCell className="text-center font-bold text-purple-600 dark:text-purple-500 text-sm py-4 px-6">{p.is_printed}</TableCell>
                    <TableCell className="text-center font-bold text-blue-600 dark:text-blue-500 text-sm py-4 px-6">{p.with_rfid}</TableCell>
                    <TableCell className="text-center font-bold text-amber-600 dark:text-amber-500 text-sm py-4 px-6 tabular-nums">{(p.is_printed || 0) - (p.with_rfid || 0)}</TableCell>
                    <TableCell className="text-right font-bold text-primary text-sm tabular-nums py-4 px-6 sticky right-[56px] bg-slate-50 dark:bg-slate-800/50 shadow-[-12px_0_15px_-10px_rgba(0,0,0,0.05)] z-20 before:absolute before:inset-y-0 before:left-0 before:w-[1px] before:bg-slate-200 dark:before:bg-slate-700">
                      {formatRp(p.total_revenue_paid || 0)}
                    </TableCell>
                    <TableCell className="sticky right-0 bg-slate-50 dark:bg-slate-800/50 z-20"></TableCell>
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </div>
          {filteredInstitutions.length > itemsPerPage && (
            <div className="flex items-center justify-between px-6 py-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-800">
              <div className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-2">
                {showAllParticipants ? (
                  <span>Menampilkan semua <span className="font-medium text-slate-700 dark:text-slate-300">{filteredInstitutions.length}</span> institusi</span>
                ) : (
                  <span>Menampilkan <span className="font-medium text-slate-700 dark:text-slate-300">{(participantPage - 1) * itemsPerPage + 1}</span> hingga <span className="font-medium text-slate-700 dark:text-slate-300">{Math.min(participantPage * itemsPerPage, filteredInstitutions.length)}</span> dari <span className="font-medium text-slate-700 dark:text-slate-300">{filteredInstitutions.length}</span> institusi</span>
                )}
                <Button 
                  variant="link" 
                  size="sm" 
                  onClick={() => setShowAllParticipants(!showAllParticipants)} 
                  className="h-auto p-0 text-primary hover:text-primary/80 font-medium ml-1"
                >
                  {showAllParticipants ? "(Tampilkan Lebih Sedikit)" : "(Tampilkan Semua)"}
                </Button>
              </div>
              {!showAllParticipants && (
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setParticipantPage(prev => Math.max(prev - 1, 1))}
                    disabled={participantPage === 1}
                    className="h-8"
                  >
                    Sebelumnya
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setParticipantPage(prev => Math.min(prev + 1, Math.ceil(filteredInstitutions.length / itemsPerPage)))}
                    disabled={participantPage >= Math.ceil(filteredInstitutions.length / itemsPerPage)}
                    className="h-8"
                  >
                    Selanjutnya
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="pt-4 border-t border-border">
        <h3 className="text-lg font-semibold mb-3 flex items-center">
          <Users className="w-5 h-5 mr-2 text-primary" /> 
          Ringkasan Tamu & Panitia
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard title="Total Tamu & Panitia" value={g.total} bgClass="bg-gradient-to-br from-slate-50 dark:from-slate-800 to-slate-100 dark:to-slate-800/80" icon={<Users className="w-5 h-5 text-slate-600 dark:text-slate-300" />} />
          <StatCard title="Terdaftar RFID" value={g.with_rfid} bgClass="bg-gradient-to-br from-blue-50 dark:from-blue-900/20 to-indigo-50 dark:to-indigo-900/20" icon={<CreditCard className="w-5 h-5 text-blue-600 dark:text-blue-500" />} />
          <StatCard title="Kartu Tercetak" value={g.is_printed} bgClass="bg-gradient-to-br from-fuchsia-50 dark:from-fuchsia-900/20 to-purple-50 dark:to-purple-900/20" icon={<Ticket className="w-5 h-5 text-purple-600 dark:text-purple-500" />} />
          <StatCard title="Terkirim Email" value={g.is_emailed} bgClass="bg-gradient-to-br from-amber-50 dark:from-amber-900/20 to-orange-50 dark:to-orange-900/20" icon={<Mail className="w-5 h-5 text-amber-600" />} />
        </div>

        {g.per_category.length > 0 && (
          <div className="mt-6">
            <h4 className="text-sm font-semibold text-slate-500 dark:text-slate-400 mb-4 uppercase tracking-wider">Rincian Jenis Kartu Tamu & Panitia</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {g.per_category.map((c) => (
                <div 
                  key={c.id} 
                  className="bg-white dark:bg-slate-900 p-5 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800 relative overflow-hidden group flex flex-col justify-between min-h-[110px]"
                >
                  <div 
                    className="absolute top-0 right-0 w-24 h-24 rounded-full -mr-8 -mt-8 opacity-10 group-hover:opacity-20 transition-opacity"
                    style={{ backgroundColor: c.color_hex || '#6366F1' }}
                  />
                  <div className="flex items-start gap-2 mb-2 z-10">
                    <div className="w-3 h-3 rounded-full mt-1 shrink-0" style={{ backgroundColor: c.color_hex || '#6366F1' }} />
                    <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest leading-tight" title={c.name}>{c.name}</p>
                  </div>
                  <p className="text-3xl font-black text-slate-900 dark:text-white z-10">{c.total}</p>
                  <div className="text-[10px] text-slate-400 font-medium mt-1 z-10">
                    Tercetak: <span className="text-emerald-500 font-bold">{c.is_printed}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      <div className="pt-4 border-t border-border mt-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 gap-4">
          <h3 className="text-lg font-bold flex items-center text-slate-800 dark:text-white">
            <div className="bg-primary/10 p-2 rounded-lg mr-3">
              <Building2 className="w-5 h-5 text-primary" /> 
            </div>
            Top Institusi / Universitas Teratas (Tamu & Panitia)
          </h3>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 shrink-0">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
              <Input 
                placeholder="Cari institusi tamu..." 
                value={guestSearchInst}
                onChange={(e) => {
                  setGuestSearchInst(e.target.value);
                  setGuestPage(1);
                }}
                className="pl-9 h-9 text-sm w-full sm:w-64 bg-white dark:bg-slate-900"
              />
            </div>
            <Button variant="outline" size="sm" onClick={handleExportGuestInstitutions} className="text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-900 h-9">
              <Download className="w-4 h-4 mr-2" />
              Export Institusi
            </Button>
          </div>
        </div>
        
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700/60 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-slate-50 dark:bg-slate-800/50">
                <TableRow className="border-b border-slate-100 dark:border-slate-800 hover:bg-transparent">
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 py-4 px-6">Nama Institusi</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 text-center py-4 px-6">Total Orang</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-purple-600 dark:text-purple-500 text-center py-4 px-6">Tercetak</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-blue-600 dark:text-blue-500 text-center py-4 px-6">Scan RFID</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-amber-600 dark:text-amber-500 text-center py-4 px-6">
                    <div className="flex items-center justify-center gap-1">
                      Selisih
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="cursor-help inline-flex items-center">
                            <Info className="w-3.5 h-3.5 opacity-70" />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Selisih (Tercetak - Scan RFID)</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </TableHead>
                  <TableHead className="py-4 px-6 w-14 sticky right-0 bg-slate-50 dark:bg-slate-800/50 z-20 shadow-[-12px_0_15px_-10px_rgba(0,0,0,0.05)] before:absolute before:inset-y-0 before:left-0 before:w-[1px] before:bg-slate-200 dark:before:bg-slate-800"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(showAllGuests ? filteredGuestInstitutions : filteredGuestInstitutions.slice((guestPage - 1) * itemsPerPage, guestPage * itemsPerPage)).map((inst, idx) => (
                  <React.Fragment key={idx}>
                  <TableRow 
                    className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors border-b border-slate-100 dark:border-slate-800/50 last:border-0 cursor-pointer group"
                    onClick={() => toggleGuestRow(inst.institution)}
                  >
                    <TableCell className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <span className="text-[13px] font-medium text-slate-400 dark:text-slate-500 w-5 tabular-nums text-right shrink-0">{String(idx + 1).padStart(2, '0')}</span>
                        <div className={`flex items-center justify-center w-5 h-5 rounded transition-transform duration-200 ${expandedGuests[inst.institution] ? 'text-primary rotate-90' : 'text-slate-300 dark:text-slate-600 group-hover:text-slate-500 dark:text-slate-400'} shrink-0`}>
                          <ChevronRight className="w-4 h-4" />
                        </div>
                        <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700/60 flex items-center justify-center shrink-0 shadow-sm">
                          <Building2 className="w-3.5 h-3.5 text-slate-500 dark:text-slate-400" />
                        </div>
                        <span className="font-medium text-slate-700 dark:text-slate-200 text-[14px] group-hover:text-slate-900 dark:group-hover:text-white transition-colors">{inst.institution}</span>
                      </div>
                    </TableCell>
                    <TableCell className="py-4 px-6 text-center">
                      <span className="text-[14px] font-semibold text-slate-700 dark:text-slate-200 tabular-nums">
                        {inst.total}
                      </span>
                    </TableCell>
                    <TableCell className="py-4 px-6 text-center">
                      <div className="flex items-center justify-center gap-2">
                        {inst.is_printed > 0 ? (
                          <>
                            <div className="w-1.5 h-1.5 rounded-full bg-purple-500 shadow-[0_0_8px_rgba(168,85,247,0.5)]"></div>
                            <span className="text-[14px] font-semibold text-purple-700 tabular-nums">{inst.is_printed}</span>
                          </>
                        ) : (
                          <span className="text-slate-300 dark:text-slate-600 font-medium">-</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="py-4 px-6 text-center">
                      <div className="flex items-center justify-center gap-2">
                        {inst.with_rfid > 0 ? (
                          <>
                            <div className="w-1.5 h-1.5 rounded-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.5)]"></div>
                            <span className="text-[14px] font-semibold text-blue-700 tabular-nums">{inst.with_rfid}</span>
                          </>
                        ) : (
                          <span className="text-slate-300 dark:text-slate-600 font-medium">-</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="py-4 px-6 text-center">
                      <span className={`text-[14px] font-semibold tabular-nums ${(inst.is_printed || 0) - (inst.with_rfid || 0) > 0 ? 'text-amber-600 dark:text-amber-500' : 'text-slate-700 dark:text-slate-200'}`}>
                        {(inst.is_printed || 0) - (inst.with_rfid || 0)}
                      </span>
                    </TableCell>
                    <TableCell className="py-4 px-4 text-center sticky right-0 bg-white dark:bg-slate-900 group-hover:bg-slate-50 dark:group-hover:bg-slate-800/50 z-10 transition-colors shadow-[-12px_0_15px_-10px_rgba(0,0,0,0.05)] before:absolute before:inset-y-0 before:left-0 before:w-[1px] before:bg-slate-100 dark:before:bg-slate-800/50">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8 text-slate-400 dark:text-slate-500 hover:text-primary hover:bg-primary/10 rounded-full"
                        onClick={(e) => { e.stopPropagation(); onGuestInstitutionClick?.(inst.institution); }}
                        title="Lihat Detail Tamu"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                  {expandedGuests[inst.institution] && inst.breakdown && inst.breakdown.length > 0 && (
                    <>
                      {inst.breakdown.map((b, i) => (
                        <TableRow key={`sub-${idx}-${i}`} className="bg-slate-50/50 dark:bg-slate-800/40 border-b border-slate-200 dark:border-slate-700/60 hover:bg-slate-100/50 dark:hover:bg-slate-800/60 transition-colors">
                          <TableCell className="py-2.5 px-6">
                            <div className="flex items-center gap-3 pl-[6.5rem]">
                              <CornerDownRight className="w-4 h-4 text-slate-300 dark:text-slate-600 shrink-0" />
                              <span className="text-[13px] font-medium text-slate-600 dark:text-slate-300">{b.category}</span>
                            </div>
                          </TableCell>
                          <TableCell className="py-2.5 px-6 text-center">
                            <span className="text-[13px] font-semibold text-slate-600 dark:text-slate-300 tabular-nums">{b.count}</span>
                          </TableCell>
                          <TableCell className="py-2.5 px-6 text-center">
                            <span className="text-[13px] font-semibold text-purple-600 dark:text-purple-500 tabular-nums">{b.is_printed > 0 ? b.is_printed : <span className="text-slate-300 dark:text-slate-600 font-medium">-</span>}</span>
                          </TableCell>
                          <TableCell className="py-2.5 px-6 text-center">
                            <span className="text-[13px] font-semibold text-blue-600 dark:text-blue-500 tabular-nums">{b.with_rfid > 0 ? b.with_rfid : <span className="text-slate-300 dark:text-slate-600 font-medium">-</span>}</span>
                          </TableCell>
                          <TableCell className="py-2.5 px-6 text-center">
                            <span className={`text-[13px] font-semibold tabular-nums ${(b.is_printed || 0) - (b.with_rfid || 0) > 0 ? 'text-amber-600 dark:text-amber-500' : 'text-slate-600 dark:text-slate-300'}`}>
                              {(b.is_printed || 0) - (b.with_rfid || 0) > 0 ? (b.is_printed || 0) - (b.with_rfid || 0) : <span className="text-slate-300 dark:text-slate-600 font-medium">-</span>}
                            </span>
                          </TableCell>
                          <TableCell className="py-2.5 px-4 sticky right-0 bg-slate-50/50 dark:bg-slate-800/40 group-hover:bg-slate-100/50 dark:group-hover:bg-slate-800/60 z-10 transition-colors shadow-[-12px_0_15px_-10px_rgba(0,0,0,0.05)] before:absolute before:inset-y-0 before:left-0 before:w-[1px] before:bg-slate-200 dark:before:bg-slate-700/60"></TableCell>
                        </TableRow>
                      ))}
                    </>
                  )}
                  </React.Fragment>
                ))}
                {filteredGuestInstitutions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-12 text-slate-500 dark:text-slate-400">
                      <Building2 className="w-10 h-10 mx-auto mb-4 text-slate-200" />
                      <span className="font-medium text-slate-600 dark:text-slate-300">Institusi tidak ditemukan</span>
                      <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">Coba gunakan kata kunci pencarian yang lain.</p>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              {g.per_institution.length > 0 && (
                <TableFooter className="bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700">
                  <TableRow className="hover:bg-transparent">
                    <TableCell className="font-bold text-slate-700 dark:text-slate-200 text-sm py-4 px-6">TOTAL KESELURUHAN (Semua Institusi)</TableCell>
                    <TableCell className="text-center font-bold text-slate-800 dark:text-slate-100 text-sm py-4 px-6 tabular-nums">{g.total}</TableCell>
                    <TableCell className="text-center font-bold text-purple-600 dark:text-purple-500 text-sm py-4 px-6 tabular-nums">{g.is_printed}</TableCell>
                    <TableCell className="text-center font-bold text-blue-600 dark:text-blue-500 text-sm py-4 px-6 tabular-nums">{g.with_rfid}</TableCell>
                    <TableCell className="text-center font-bold text-amber-600 dark:text-amber-500 text-sm py-4 px-6 tabular-nums">{(g.is_printed || 0) - (g.with_rfid || 0)}</TableCell>
                    <TableCell className="sticky right-0 bg-slate-50 dark:bg-slate-800/50 z-20 shadow-[-12px_0_15px_-10px_rgba(0,0,0,0.05)] before:absolute before:inset-y-0 before:left-0 before:w-[1px] before:bg-slate-200 dark:before:bg-slate-700"></TableCell>
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </div>
          {filteredGuestInstitutions.length > itemsPerPage && (
            <div className="flex items-center justify-between px-6 py-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-800">
              <div className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-2">
                {showAllGuests ? (
                  <span>Menampilkan semua <span className="font-medium text-slate-700 dark:text-slate-300">{filteredGuestInstitutions.length}</span> institusi tamu</span>
                ) : (
                  <span>Menampilkan <span className="font-medium text-slate-700 dark:text-slate-300">{(guestPage - 1) * itemsPerPage + 1}</span> hingga <span className="font-medium text-slate-700 dark:text-slate-300">{Math.min(guestPage * itemsPerPage, filteredGuestInstitutions.length)}</span> dari <span className="font-medium text-slate-700 dark:text-slate-300">{filteredGuestInstitutions.length}</span> institusi</span>
                )}
                <Button 
                  variant="link" 
                  size="sm" 
                  onClick={() => setShowAllGuests(!showAllGuests)} 
                  className="h-auto p-0 text-primary hover:text-primary/80 font-medium ml-1"
                >
                  {showAllGuests ? "(Tampilkan Lebih Sedikit)" : "(Tampilkan Semua)"}
                </Button>
              </div>
              {!showAllGuests && (
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setGuestPage(prev => Math.max(prev - 1, 1))}
                    disabled={guestPage === 1}
                    className="h-8"
                  >
                    Sebelumnya
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setGuestPage(prev => Math.min(prev + 1, Math.ceil(filteredGuestInstitutions.length / itemsPerPage)))}
                    disabled={guestPage >= Math.ceil(filteredGuestInstitutions.length / itemsPerPage)}
                    className="h-8"
                  >
                    Selanjutnya
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      </div>

      {/* 3. COMBINED INSTITUTIONS TABLE */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden mt-6">
        <div className="p-5 md:p-6 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/20">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/50 border border-indigo-200 dark:border-indigo-800 flex items-center justify-center shrink-0 shadow-sm">
                <Building2 className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-800 dark:text-white">Top Institusi / Universitas Teratas (Keseluruhan)</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">Total akumulasi peserta dan tamu per institusi</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <div className="relative group w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500 group-focus-within:text-indigo-500 dark:group-focus-within:text-indigo-400 transition-colors" />
                <Input 
                  placeholder="Cari nama institusi..." 
                  value={combinedSearchInst}
                  onChange={(e) => setCombinedSearchInst(e.target.value)}
                  className="pl-9 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 focus-visible:ring-indigo-500"
                />
              </div>
              <Button onClick={handleExportCombinedInstitutions} variant="outline" className="w-full sm:w-auto bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800 border-slate-200 dark:border-slate-700">
                <Download className="w-4 h-4 mr-2" />
                Export Data
              </Button>
            </div>
          </div>
        </div>

        <div className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700 hover:bg-transparent">
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 py-4 px-6">No & Nama Institusi</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 text-center py-4 px-6 w-32">Total Peserta</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 text-center py-4 px-6 w-32">Total Tamu / Panitia</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 text-center py-4 px-6 w-32">Tercetak</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 text-center py-4 px-6 w-32">Scan RFID</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 text-center py-4 px-6 w-32">
                    <div className="flex items-center justify-center gap-1">
                      Selisih
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="cursor-help inline-flex items-center">
                            <Info className="w-3.5 h-3.5 opacity-70" />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Selisih (Tercetak - Scan RFID)</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 text-center py-4 px-6 w-32 sticky right-0 bg-slate-50 dark:bg-slate-800/50 z-20 shadow-[-12px_0_15px_-10px_rgba(0,0,0,0.05)] before:absolute before:inset-y-0 before:left-0 before:w-[1px] before:bg-slate-200 dark:before:bg-slate-800">Total Keseluruhan</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(showAllCombined ? filteredCombinedInstitutions : filteredCombinedInstitutions.slice((combinedPage - 1) * itemsPerPage, combinedPage * itemsPerPage)).map((inst, idx) => (
                  <TableRow key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors border-b border-slate-100 dark:border-slate-800/50 last:border-0 group">
                    <TableCell className="py-4 px-6">
                      <div className="flex items-center gap-4">
                        <span className="text-[13px] font-medium text-slate-400 dark:text-slate-500 w-5 tabular-nums text-right shrink-0">{String(idx + 1).padStart(2, '0')}</span>
                        <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700/60 flex items-center justify-center shrink-0 shadow-sm">
                          <Building2 className="w-3.5 h-3.5 text-slate-500 dark:text-slate-400" />
                        </div>
                        <span className="font-medium text-slate-700 dark:text-slate-200 text-[14px] group-hover:text-slate-900 dark:group-hover:text-white transition-colors">{inst.institution}</span>
                      </div>
                    </TableCell>
                    <TableCell className="py-4 px-6 text-center">
                      <span className="text-[14px] font-semibold text-slate-700 dark:text-slate-200 tabular-nums">
                        {inst.participants}
                      </span>
                    </TableCell>
                    <TableCell className="py-4 px-6 text-center">
                      <span className="text-[14px] font-semibold text-slate-700 dark:text-slate-200 tabular-nums">
                        {inst.guests}
                      </span>
                    </TableCell>
                    <TableCell className="py-4 px-6 text-center">
                      <span className="text-[14px] font-semibold text-slate-700 dark:text-slate-200 tabular-nums">
                        {inst.printed}
                      </span>
                    </TableCell>
                    <TableCell className="py-4 px-6 text-center">
                      <span className="text-[14px] font-semibold text-slate-700 dark:text-slate-200 tabular-nums">
                        {inst.rfid}
                      </span>
                    </TableCell>
                    <TableCell className="py-4 px-6 text-center">
                      <span className={`text-[14px] font-semibold tabular-nums ${(inst.printed || 0) - (inst.rfid || 0) > 0 ? 'text-amber-600 dark:text-amber-500' : 'text-slate-700 dark:text-slate-200'}`}>
                        {(inst.printed || 0) - (inst.rfid || 0)}
                      </span>
                    </TableCell>
                    <TableCell className="py-4 px-6 text-center sticky right-0 bg-white dark:bg-slate-900 group-hover:bg-slate-50 dark:group-hover:bg-slate-800/50 z-10 transition-colors shadow-[-12px_0_15px_-10px_rgba(0,0,0,0.05)] before:absolute before:inset-y-0 before:left-0 before:w-[1px] before:bg-slate-100 dark:before:bg-slate-800/50">
                      <span className="text-[14px] font-bold text-indigo-600 dark:text-indigo-400 tabular-nums bg-indigo-50 dark:bg-indigo-900/20 px-3 py-1 rounded-full border border-indigo-100 dark:border-indigo-800/50">
                        {inst.total}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredCombinedInstitutions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-12 text-slate-500 dark:text-slate-400">
                      <Building2 className="w-10 h-10 mx-auto mb-4 text-slate-200" />
                      <span className="font-medium text-slate-600 dark:text-slate-300">Institusi tidak ditemukan</span>
                      <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">Coba gunakan kata kunci pencarian yang lain.</p>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              {combinedInstitutions.length > 0 && (() => {
                const totalP = combinedInstitutions.reduce((acc, curr) => acc + (curr.participants || 0), 0);
                const totalG = combinedInstitutions.reduce((acc, curr) => acc + (curr.guests || 0), 0);
                const totalPrinted = combinedInstitutions.reduce((acc, curr) => acc + (curr.printed || 0), 0);
                const totalRfid = combinedInstitutions.reduce((acc, curr) => acc + (curr.rfid || 0), 0);
                const totalAll = combinedInstitutions.reduce((acc, curr) => acc + (curr.total || 0), 0);
                
                return (
                  <TableFooter className="bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700">
                    <TableRow className="hover:bg-transparent">
                      <TableCell className="font-bold text-slate-700 dark:text-slate-200 text-sm py-4 px-6">TOTAL KESELURUHAN (Semua Institusi)</TableCell>
                      <TableCell className="text-center font-bold text-slate-800 dark:text-slate-100 text-sm py-4 px-6 tabular-nums">{totalP}</TableCell>
                      <TableCell className="text-center font-bold text-slate-800 dark:text-slate-100 text-sm py-4 px-6 tabular-nums">{totalG}</TableCell>
                      <TableCell className="text-center font-bold text-slate-800 dark:text-slate-100 text-sm py-4 px-6 tabular-nums">{totalPrinted}</TableCell>
                      <TableCell className="text-center font-bold text-slate-800 dark:text-slate-100 text-sm py-4 px-6 tabular-nums">{totalRfid}</TableCell>
                      <TableCell className="text-center font-bold text-amber-600 dark:text-amber-500 text-sm py-4 px-6 tabular-nums">{totalPrinted - totalRfid}</TableCell>
                      <TableCell className="text-center font-bold text-indigo-600 dark:text-indigo-400 text-sm py-4 px-6 tabular-nums sticky right-0 bg-slate-50 dark:bg-slate-800/50 z-20 shadow-[-12px_0_15px_-10px_rgba(0,0,0,0.05)] before:absolute before:inset-y-0 before:left-0 before:w-[1px] before:bg-slate-200 dark:before:bg-slate-700">{totalAll}</TableCell>
                    </TableRow>
                  </TableFooter>
                );
              })()}
            </Table>
          </div>
          {filteredCombinedInstitutions.length > itemsPerPage && (
            <div className="flex items-center justify-between px-6 py-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-800">
              <div className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-2">
                {showAllCombined ? (
                  <span>Menampilkan semua <span className="font-medium text-slate-700 dark:text-slate-300">{filteredCombinedInstitutions.length}</span> institusi gabungan</span>
                ) : (
                  <span>Menampilkan <span className="font-medium text-slate-700 dark:text-slate-300">{(combinedPage - 1) * itemsPerPage + 1}</span> hingga <span className="font-medium text-slate-700 dark:text-slate-300">{Math.min(combinedPage * itemsPerPage, filteredCombinedInstitutions.length)}</span> dari <span className="font-medium text-slate-700 dark:text-slate-300">{filteredCombinedInstitutions.length}</span> institusi</span>
                )}
                <Button 
                  variant="link" 
                  size="sm" 
                  onClick={() => setShowAllCombined(!showAllCombined)} 
                  className="h-auto p-0 text-primary hover:text-primary/80 font-medium ml-1"
                >
                  {showAllCombined ? "(Tampilkan Lebih Sedikit)" : "(Tampilkan Semua)"}
                </Button>
              </div>
              {!showAllCombined && (
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setCombinedPage(prev => Math.max(prev - 1, 1))}
                    disabled={combinedPage === 1}
                    className="h-8"
                  >
                    Sebelumnya
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setCombinedPage(prev => Math.min(prev + 1, Math.ceil(filteredCombinedInstitutions.length / itemsPerPage)))}
                    disabled={combinedPage >= Math.ceil(filteredCombinedInstitutions.length / itemsPerPage)}
                    className="h-8"
                  >
                    Selanjutnya
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, icon, valueClassName = '', bgClass = 'bg-white dark:bg-slate-900' }: { title: string; value: React.ReactNode; icon: React.ReactNode; valueClassName?: string; bgClass?: string }) {
  return (
    <Card className={`overflow-hidden shadow-sm transition-all duration-300 hover:shadow-md hover:-translate-y-1 ${bgClass}`}>
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-1 px-4 pt-4 md:px-5 md:pt-5">
        <CardTitle className="text-[13px] font-medium text-slate-500 dark:text-slate-400 dark:text-slate-500 leading-tight pr-2 line-clamp-2">
          {title}
        </CardTitle>
        <div className="p-1.5 bg-white dark:bg-slate-900/60 dark:bg-black/20 rounded-md shadow-sm shrink-0">
          {icon}
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-4 md:px-5 md:pb-5 mt-1">
        <div className={`text-xl lg:text-2xl font-semibold tracking-tight truncate ${valueClassName}`}>{value}</div>
      </CardContent>
    </Card>
  );
}
